import { Link } from '@tanstack/react-router';
import { ChevronRight, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { PLATFORM_LOGO } from '~/components/common/chips/platform-logo';

import type { BatchHeader } from '../../types/catalog-operations.types';

interface Props {
  header: BatchHeader;
  batchId: string;
  /** Retry delivery titles the page "Redeliver" instead of "Deliver". */
  isRedelivery?: boolean;
}

/**
 * Breadcrumb + identity header for the delivery wizard. The destination is
 * inline text next to the title (logo + label), not a chip — a chip reads as
 * clickable, and this is just naming where the batch is going. No subtitle:
 * the wizard's own per-step hints (in `DeliveryWizardStepper`) carry that
 * explanation instead of a redundant paragraph above them. The breadcrumb
 * stops at the batch name — the current page doesn't need its own trailing
 * crumb when the H1 right below already names it.
 */
export function DeliveryWizardHeader({ header, batchId, isRedelivery = false }: Props) {
  const { t } = useTranslation('common');
  const logo = PLATFORM_LOGO[header.sourcePlatform];
  return (
    <div className="space-y-2">
      <nav className="flex flex-wrap items-center gap-1.5 text-sm">
        <Link
          data-testid="delivery-wizard-breadcrumb-root-link"
          to="/catalog-operations"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 transition-colors hover:underline"
        >
          <Zap className="h-4 w-4 text-amber-500" aria-hidden="true" />
          <span>{t('deliveryWizard.header.breadcrumbRoot')}</span>
        </Link>
        <ChevronRight className="text-muted-foreground h-4 w-4" aria-hidden="true" />
        <Link
          to="/catalog-operations/$batchId"
          params={{ batchId }}
          data-testid="delivery-wizard-breadcrumb-batch-link"
          className="text-muted-foreground hover:text-foreground transition-colors hover:underline"
        >
          {header.name.trim() !== '' ? header.name : header.batchId}
        </Link>
      </nav>

      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-2xl font-bold tracking-tight" data-testid="delivery-wizard-title">
          {isRedelivery
            ? t('deliveryWizard.header.redeliverTitle')
            : t('deliveryWizard.header.title')}
        </h1>
        <span className="text-muted-foreground inline-flex items-center gap-1.5 text-base">
          {t('deliveryWizard.header.to')}
          <span
            className="text-foreground inline-flex items-center gap-1.5 font-medium"
            data-testid="delivery-wizard-destination"
          >
            {logo && <img src={logo} alt="" className="h-[18px] w-[18px] object-contain" />}
            {header.source}
          </span>
        </span>
      </div>
    </div>
  );
}
